import { useState } from 'react';
import { registerAssetClasses, type Instrument, type MarginLevels, type Ticker } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { useChannel, ws } from '../lib/ws';

export interface Meta {
  swapHourUtc: number;
  swapTripleDay: number;
  demoBalance: string;
}

interface MarketState {
  loaded: boolean;
  error: string | null;
  instruments: Instrument[];
  bySymbol: Record<string, Instrument>;
  tickers: Record<string, Ticker>;
  meta: Meta | null;
  /** Position margin levels for warnings (fractions); null until loaded. */
  marginLevels: MarginLevels | null;
}

export const marketStore = createStore<MarketState>({
  loaded: false,
  error: null,
  instruments: [],
  bySymbol: {},
  tickers: {},
  meta: null,
  marginLevels: null,
});

function applyTickers(list: Ticker[]) {
  if (!Array.isArray(list)) return;
  marketStore.set((s) => {
    const next = { ...s.tickers };
    for (const t of list) next[t.s] = t;
    return { tickers: next };
  });
}

let started = false;
export async function loadMarket() {
  if (started) return;
  started = true;
  ws.subscribe('tickers', applyTickers);
  try {
    const [instruments, tickers, meta] = await Promise.all([
      api.get<Instrument[]>('/api/instruments'),
      api.get<Ticker[]>('/api/tickers'),
      api.get<Meta>('/api/meta'),
    ]);
    registerAssetClasses(instruments);
    marketStore.set({ instruments, bySymbol: Object.fromEntries(instruments.map((i) => [i.symbol, i])), meta, loaded: true, error: null });
    applyTickers(tickers);
    api
      .get<MarginLevels>('/api/settings/margin')
      .then((marginLevels) => marketStore.set({ marginLevels }))
      .catch(() => {});
  } catch (e) {
    started = false;
    marketStore.set({ error: (e as Error).message });
    window.setTimeout(loadMarket, 3000);
  }
}

export const useInstruments = () => useStore(marketStore, (s) => s.instruments);
export const useMarginLevels = () => useStore(marketStore, (s) => s.marginLevels);
export const useInstrument = (sym: string) => useStore(marketStore, (s) => s.bySymbol[sym] ?? null);
export const useTicker = (sym: string) => useStore(marketStore, (s) => s.tickers[sym] ?? null);
export const useTickers = () => useStore(marketStore, (s) => s.tickers);
export const useMeta = () => useStore(marketStore, (s) => s.meta);
export const useMarketLoaded = () => useStore(marketStore, (s) => s.loaded);

export interface LiveQuote {
  bid: string;
  ask: string;
  mark: string;
  t: number;
  /** USD per unit of the price currency; absent for USD-priced instruments. */
  fx?: string;
}

/** Fast quote stream (≈4/s) for one symbol; falls back to the 1/s ticker until the first message. */
export function useQuote(sym: string): LiveQuote | null {
  const ticker = useTicker(sym);
  const [q, setQ] = useState<{ sym: string; q: LiveQuote } | null>(null);
  useChannel<LiveQuote>(sym ? `price:${sym}` : null, (d) => setQ({ sym, q: d }));
  if (q && q.sym === sym && (!ticker || q.q.t >= ticker.t)) return q.q;
  return ticker ? { bid: ticker.bid, ask: ticker.ask, mark: ticker.mark, t: ticker.t, fx: ticker.fx } : null;
}
